/** Mutations -
 * Asset Editor Mutations (data changes)
 * Used via $store.commit()
 * @returns {Object}
 */
import app from 'app';
import { getAnalyticsData, getDefaultPages } from './assetEditorApi.js';
import RestrictionsController from 'c/RestrictionsController';
import { getAudioTracksBetweenSpan } from 'v/Story/Timeline/api/asset';

export default {
  /* Set AGD
   * Arguments agdAttr (Object) {agdKey: agdValue}
   * assigns to workModel
   */
  setAGD: (state, agdAttr) => {
    _.each(agdAttr, function (agdVal, agdKey) {
      app.Vue.set(state.workModel.agd, agdKey, agdVal);
    });
  },

  setTimes: (state, obj) => {
    const acceptedKeys = [
      'endTime',
      'duration',
      'displayDuration',
      'displayEndTime',
      'displayStartTime',
    ];
    _.each(obj, function (value, key) {
      if (acceptedKeys.includes(key)) {
        app.Vue.set(state.workModel, key, value);
      }
    });
  },

  setTextImportOptions: (state, options) => {
    app.Vue.set(state, 'textImportOptions', options);
  },

  setAssetItemSchemaModel: (state, changes) => {
    app.Vue.set(
      state.workModel,
      'assetItemSchemaDataModel',
      Object.assign({}, state.workModel.assetItemSchemaDataModel, changes)
    );
  },

  /* Set Tmp AGD Object
   * Arguments agdAttr (Object) {agdKey: agdValue}
   */
  setTMPAGD: (state, agdAttr) => {
    _.each(agdAttr, function (agdVal, agdKey) {
      app.Vue.set(state.tmpAGD, agdKey, agdVal);
    });
  },

  setActiveOverlayTrack: (state, guid) => {
    state.activeOverlayTrackGuid = guid;
  },

  disableMouse: (state) => {
    state.mouseDisabled = true;
  },

  enableMouse: (state) => {
    state.mouseDisabled = false;
  },

  applyOverlayTracks: (state) => {
    if (_.isArray(state.tempTracks)) {
      state.workModel.overlayTracks = _.cloneDeepAndClean(state.tempTracks);
    }
  },

  setTempTracks: (state) => {
    state.tempTracks = _.cloneDeepAndClean(state.workModel.overlayTracks);
  },

  resetTempTracks: (state) => {
    state.tempTracks = null;
    state.activeOverlayTrackGuid = null;
  },

  deleteTempTextOverlay: (state, textOverlayId) => {
    const textOverlayIndex = _.findIndex(state.tmpAGD.textOverlays, {
      id: textOverlayId,
    });
    state.tmpAGD.textOverlays.splice(textOverlayIndex, 1);
  },

  addTempTextOverlay: (state, textOverlay) => {
    if (_.isEmpty(state.tmpAGD.textOverlays)) {
      app.Vue.set(state.tmpAGD, 'textOverlays', []);
    }
    state.tmpAGD.textOverlays.push(textOverlay);
  },

  alignTextOverlaysLeft: (state, textOverlay) => {
    if (
      _.isEmpty(state.tmpAGD.textOverlays) ||
      state.tmpAGD.textOverlays.length === 1
    ) {
      return;
    }
    const isVisible = (to) =>
      to.animationStartTime <= state.timeInAsset &&
      to.animationEndTime >= state.timeInAsset;
    const textOverlays = [...state.tmpAGD.textOverlays];
    const newOffsetX = textOverlay
      ? textOverlay.offsetX
      : _.minBy(textOverlays, 'offsetX').offsetX;

    textOverlays.forEach((textOverlay) => {
      if (isVisible(textOverlay)) {
        textOverlay.offsetX = newOffsetX;
      }
    });

    state.tmpAGD.textOverlays = textOverlays;
  },

  alignTextOverlaysRight: (state, textOverlay) => {
    if (
      _.isEmpty(state.tmpAGD.textOverlays) ||
      state.tmpAGD.textOverlays.length === 1
    ) {
      return;
    }
    const isVisible = (to) =>
      to.animationStartTime <= state.timeInAsset &&
      to.animationEndTime >= state.timeInAsset;
    const textOverlays = [...state.tmpAGD.textOverlays];
    const selectedTo =
      textOverlay || _.maxBy(textOverlays, (to) => to.offsetX + to.width);
    const rightOffset = selectedTo.offsetX + selectedTo.width;

    textOverlays.forEach((textOverlay) => {
      if (isVisible(textOverlay)) {
        textOverlay.offsetX = rightOffset - textOverlay.width;
      }
    });

    state.tmpAGD.textOverlays = textOverlays;
  },

  distributeTextOverlays: (state) => {
    if (
      _.isEmpty(state.tmpAGD.textOverlays) ||
      state.tmpAGD.textOverlays.length === 1
    ) {
      return;
    }
    const isVisible = (to) =>
      to.animationStartTime <= state.timeInAsset &&
      to.animationEndTime >= state.timeInAsset;
    const textOverlays = [...state.tmpAGD.textOverlays];
    const visibleTextOverlays = textOverlays
      .filter(isVisible)
      .sort((o1, o2) => o2.offsetY < o1.offsetY);
    const minTop = visibleTextOverlays[0].offsetY;
    const maxBottomObj = _.maxBy(
      visibleTextOverlays,
      (to) => to.offsetY + to.height
    );
    const maxBottom = maxBottomObj.offsetY + maxBottomObj.height;
    const availableHeight = maxBottom - minTop;
    const totalHeight = _.sumBy(visibleTextOverlays, (to) => to.height);
    const marginBottom =
      (availableHeight - totalHeight) / (visibleTextOverlays.length - 1);

    visibleTextOverlays.forEach((textOverlay, index) => {
      if (index > 0) {
        const prev = visibleTextOverlays[index - 1];
        textOverlay.offsetY = prev.offsetY + prev.height + marginBottom;
      }
    });

    state.tmpAGD.textOverlays = textOverlays;
  },

  setTempTextOverlay: (state, textOverlayAttr) => {
    const id = textOverlayAttr.id;
    const textOverlay = _.find(state.tmpAGD.textOverlays, { id });
    if (textOverlay) {
      _.each(textOverlayAttr, function (val, key) {
        app.Vue.set(textOverlay, key, val);
      });
    }
  },

  resetInAssetNavigationTOModel: (state) => {
    state.ianToModel = null;
  },

  setTimeInAsset: (state, time) => {
    app.Vue.set(state, 'timeInAsset', time);
  },

  setTimeInAssetToFirstTextOverlay: (state) => {
    const tos = state.tmpAGD.textOverlays || [];
    const min = tos.length ? _.minBy(tos, (t) => t.animationStartTime) : 0;
    app.Vue.set(state, 'timeInAsset', min.animationStartTime || 0);
  },

  setVolumeControl: (state, { volumeControl, onVoPlayVolumeControl }) => {
    const getMutedChannels = (o) =>
      Object.entries(o)
        .filter(([, value]) => !_.isNull(value))
        .map(([key]) => key);
    const getChannelsWithVolume = (o) =>
      Object.entries(volumeControl)
        .filter(([, value]) => value > 0)
        .map(([key]) => key);
    let mutedChannels = getMutedChannels(state.volumeMuted);
    let hasVolumeChannels = getChannelsWithVolume(volumeControl);

    _.intersection(mutedChannels, hasVolumeChannels).forEach((key) => {
      app.Vue.set(state.volumeMuted, key, null);
    });

    mutedChannels = getMutedChannels(state.volumeMutedOnVoPlay);
    hasVolumeChannels = getChannelsWithVolume(onVoPlayVolumeControl);

    _.intersection(mutedChannels, hasVolumeChannels).forEach((key) => {
      app.Vue.set(state.volumeMutedOnVoPlay, key, null);
    });

    app.Vue.set(state.workModel, 'volumeControl', volumeControl);
    app.Vue.set(
      state.workModel,
      'onVoPlayVolumeControl',
      onVoPlayVolumeControl
    );
  },

  playheadPosition: (state, time) => {
    app.Vue.set(state, 'playheadPosition', time);
  },

  playheadPositionToFirstTextOverlay: (state) => {
    const tos = state.tmpAGD.textOverlays || [];
    const min = tos.length ? _.minBy(tos, (t) => t.animationStartTime) : 0;
    app.Vue.set(state, 'playheadPosition', min.animationStartTime || 0);
  },

  assignTmpAGD: (state) => {
    app.Vue.set(state, 'tmpAGD', _.cloneDeep(state.workModel.agd));
  },

  /**
   * Checkout new work model
   */
  assignWorkModel: (state) => {
    // DO NOT!!!!!!±±!±±!!! use assetModel serialize!
    // causes conflicts between assetmodels, leading to unwanted RTPs
    app.Vue.set(state, 'workModel', state.assetModel.cloneAttributes());
  },
  /**
   * It is what it is.
   * Receives a new page id (String) - matching state's pages object key
   */
  setCurrentPage: (state, pageId) => {
    app.Vue.set(state, 'currentPageId', pageId);
  },
  setSwitchCOClassifications: (state, switchCOClassifications) => {
    app.Vue.set(state, '_switchCOClassifications', switchCOClassifications);
  },
  /**
   * updatePage state
   * Receives page id (String) - matching state's page object key
   * Receives pageChanges (Object) - extend the page
   */
  updatePage: (state, { pageId, pageChanges }) => {
    const pageIndex = state.pages.findIndex((p) => p.id === pageId);
    const page = state.pages[pageIndex];

    Object.keys(pageChanges).forEach((key) => {
      app.Vue.set(page, key, pageChanges[key]);
    });
  },

  setTmpPageState: (state, extendedProps) => {
    app.Vue.set(
      state,
      'pageTmpState',
      Object.assign({}, state.pageTmpState, extendedProps)
    );
  },

  resetTmpPageState: (state) => {
    app.Vue.set(state, 'pageTmpState', {});
  },

  /**
   * Assign a new RealTimePreview to state
   * Receives RTPModel
   */
  assignRTPModel: (state, rtpModel) => {
    app.Vue.set(state, 'rtpModel', rtpModel);
  },

  assignSceneLayoutSTPModel: (state, sceneLayoutSTPModel) => {
    app.Vue.set(state, 'sceneLayoutSTPModel', sceneLayoutSTPModel);
  },
  /**
   * Exextends StarticPreview in state
   */
  assignSTP: (state, stp) => {
    app.Vue.set(state, 'stp', Object.assign({}, state.stp, stp));
  },
  /**
   * Sets the original asset properties (set by Stage.vue)
   */
  setOriginalAsset: (state, extendedProps) => {
    app.Vue.set(
      state,
      'originalAsset',
      Object.assign({}, state.originalAsset, extendedProps)
    );
  },

  addTextStyle: (state, newTextStyle) => {
    state.story.textStyles.push(newTextStyle);
  },

  updateTextStyle: (state, o) => {
    const id = o.id;
    const newId = o.newId;
    const style = _.find(state.story.textStyles, { id });
    if (id && newId) {
      app.Vue.set(style, 'id', newId);
    }
    Object.keys(_.omit(o, ['id', 'newId'])).forEach((key) => {
      app.Vue.set(style, key, o[key]);
    });
  },

  updateApplyBoxDesign: (state, o) => {
    const id = o.id;
    const style = _.find(state.story.textStyles, { id });
    app.Vue.set(style.box, 'applyCheckbox', o.checked);
  },

  updateApplyPosition: (state, o) => {
    const id = o.id;
    const style = _.find(state.story.textStyles, { id });
    app.Vue.set(style.position, 'applyCheckbox', o.checked);
  },

  deleteTextStyle: (state, id) => {
    const index = _.findIndex(state.story.textStyles, (s) => s.id === id);
    state.story.textStyles.splice(index, 1);
    app.api.textStyles.delete(id).done(() => {
      // TODO error recovery
    });
  },

  addOrUpdateCustomOverlayField: (state, o) => {
    const currentPage = state.pages.find((p) => p.id === state.currentPageId);
    const currentOverlayId = currentPage?.id;
    const currentOverlayName = currentPage?.displayText || currentPage?.name;
    const noRealtimePreview = currentPage?.noRealtimePreview;

    const co =
      state.tmpAGD.customOverlays &&
      state.tmpAGD.customOverlays.length &&
      state.tmpAGD.customOverlays.find(
        (customOverlay) => customOverlay.id === currentOverlayId
      );

    const omitFn = (v, k) => k === 'id' || k === 'layers';

    if (co) {
      const index = _.findIndex(co.fields, (field) => field.name === o.name);

      // if already exist, extend the new object with existing one
      if (index > -1) {
        const field = co.fields[index];
        if (!_.isUndefined(o.value) && !_.isNull(o.value)) {
          field.value = o.value;
        }
        if (o.fieldDataModel) {
          field.fieldDataModel = o.fieldDataModel;
        }
        // only AREA fields opting into the show/hide toggle carry `active`, and
        // isBoolean is what keeps the key off every field that does not send it.
        // Vue.set because it is a new property on an already reactive object
        if (_.isBoolean(o.active)) {
          app.Vue.set(field, 'active', o.active);
        }
        return;
      }

      if (!co.fields) {
        app.Vue.set(co, 'fields', []);
      }

      // if first time agd, push it to the array
      co.fields.push(_.omitBy(o, omitFn));
    } else {
      if (!state.tmpAGD.customOverlays) {
        app.Vue.set(state.tmpAGD, 'customOverlays', []);
      }
      state.tmpAGD.customOverlays.push({
        id: o.id,
        fields: [_.omitBy(o, omitFn)],
        layers: o.layers,
        displayText: currentOverlayName,
        noRealtimePreview,
      });
    }
  },

  /**
   * Stamps the object tracking job id on a custom overlay.
   * Written to tmpAGD so it rides into workModel.agd through
   * CustomOverlaysFields#getNewAgdCustomOverlays on apply.
   */
  setCustomOverlayObjectTrackingId: (state, { id, objectTrackingId }) => {
    const co = _.find(state.tmpAGD.customOverlays || [], { id });

    if (co) {
      app.Vue.set(co, 'objectTrackingId', objectTrackingId);
    } else {
      // dropping the id here would leave the overlay silently untracked
      app.doctorController.report(
        `setCustomOverlayObjectTrackingId: no overlay ${id} in tmpAGD`
      );
    }
  },

  setObjectTrackingInFlight: (state, inFlight = false) => {
    state.objectTrackingInFlight = inFlight;
  },

  setApplyVolumeToAll: (state, shouldApply = false) => {
    state.applyVolumeToAll = shouldApply;
  },

  setIsValidToLeave: (state, isValidToLeave = true) => {
    state.isValidToLeave = isValidToLeave;
  },

  setConfirmedVolumeApplyAll: (state, confirmed = true) => {
    state.confirmedVolumeApplyAll = confirmed;
  },

  setShouldPreviewAutoplay: (state, shouldPreviewAutoplay = false) => {
    state.shouldPreviewAutoplay = shouldPreviewAutoplay;
  },

  setOldDuration(state, oldDuration) {
    state.oldDuration = oldDuration;
  },

  setMetadataDuration(state, duration) {
    app.Vue.set(state.workModel.metadata, 'duration', duration);
  },

  setNewDuration(state, newDuration) {
    state.newDuration = newDuration;
  },

  sendToBack(state, textOverlayId) {
    if (_.isEmpty(state.tmpAGD.textOverlays)) {
      return;
    }
    const currentTextOverlay = state.tmpAGD.textOverlays.find(
      (t) => t.id === textOverlayId
    );
    const currentZIndex = currentTextOverlay.zIndex;
    for (const textOverlay of state.tmpAGD.textOverlays) {
      if (textOverlay.zIndex < currentZIndex) {
        textOverlay.zIndex = textOverlay.zIndex + 1;
      }
    }
    currentTextOverlay.zIndex = 0;
  },

  bringToFront(state, textOverlayId) {
    if (_.isEmpty(state.tmpAGD.textOverlays)) {
      return;
    }
    const currentTextOverlay = state.tmpAGD.textOverlays.find(
      (t) => t.id === textOverlayId
    );
    const currentZIndex = currentTextOverlay.zIndex;
    for (const textOverlay of state.tmpAGD.textOverlays) {
      if (textOverlay.zIndex > currentZIndex) {
        textOverlay.zIndex = textOverlay.zIndex - 1;
      }
    }

    currentTextOverlay.zIndex = state.tmpAGD.textOverlays.length - 1;
  },

  toggleVolumeMute: (state, { channel, mute }) => {
    const toggleVolumeMuteBase = ({ volumeChannelsKey, memoryKey }) => {
      const channels = state.workModel[volumeChannelsKey];
      const rememberedVolume = state[memoryKey][channel];
      let newMemory = null;

      if (rememberedVolume === null && channels[channel] !== 0) {
        newMemory = channels[channel];
      }

      channels[channel] = mute ? 0 : rememberedVolume || 0.95;

      if (
        channel === 'narration' &&
        volumeChannelsKey === 'onVoPlayVolumeControl'
      ) {
        if (mute) {
          channels['bgMusic'] =
            state.workModel['volumeControl']?.bgMusic ?? 0.95;
        }
      }

      app.Vue.set(state[memoryKey], channel, newMemory);
      app.Vue.set(state.workModel, volumeChannelsKey, channels);
    };

    toggleVolumeMuteBase({
      volumeChannelsKey: 'volumeControl',
      memoryKey: 'volumeMuted',
    });
    toggleVolumeMuteBase({
      volumeChannelsKey: 'onVoPlayVolumeControl',
      memoryKey: 'volumeMutedOnVoPlay',
    });
  },

  toggleShowMoreInfo(state) {
    state.showMoreInfo = !state.showMoreInfo;
  },

  setSceneDetectionEnabled(state, enabled) {
    app.stash.set('fragmentEditorShowScene', enabled);
    state.sceneDetectionEnabled = enabled;
  },

  persistWorkModel(state) {
    // pick serializable attributes
    const pickedAttr = _.pick(_.cloneDeep(state.workModel), [
      'agd',
      'volumeControl',
      'onVoPlayVolumeControl',
      'displayDuration',
      'displayEndTime',
      'endTime',
      'assetItemSchemaDataModel',
      'overlayTracks',
    ]);

    // rtp model active, assign it to to model
    if (state.rtpModel) {
      state.rtpModel.set('silent', false, { silent: true });
      if (state.rtpModel.isDone()) {
        Object.assign(pickedAttr, {
          realTimePreviewChecksum: state.rtpModel.get(
            'realTimePreviewChecksum'
          ),
          realTimePreviewResultData: state.rtpModel.get(
            'realTimePreviewResultData'
          ),
          _realTimePreviewInProgress: false,
        });
      } else {
        Object.assign(pickedAttr, {
          realTimePreviewChecksum: null,
          realTimePreviewResultData: null,
          _tmpRealTimePreviewChecksum: state.rtpModel.get(
            'realTimePreviewChecksum'
          ),
          _realTimePreviewInProgress: true,
        });
      }
    } else {
      // no rtpModel = invalid realTimePreviewResultData
      Object.assign(pickedAttr, {
        realTimePreviewChecksum: null,
        realTimePreviewResultData: null,
        _realTimePreviewInProgress: false,
      });
    }

    state.assetModel.set(pickedAttr);

    if (_.isDefined(pickedAttr.displayDuration)) {
      app.namespace.Controller.AssetController.setInitialAssetDuration(
        state.assetModel,
        pickedAttr.displayDuration
      );
    }

    // apply volume to all other assets? keep AFTER model attributes set
    if (state.applyVolumeToAll) {
      app.trigger('apply.volume.to.all', state.assetModel);
    }
  },

  /**
   * set state props that are specific for current asset
   * (in context of in-asset-navigation - same module serves multiple assets - each one at a time)
   * @param state
   * @param asset
   */
  setCurrentAsset(state, { asset }) {
    // assuming assetModel already in state
    RestrictionsController.assignRestrictions(asset);
    state.initialModel = _.cloneDeepAndClean(asset);

    state.workModel = _.cloneDeepAndClean(state.initialModel);
    state.assetModel = new app.namespace.Model.AssetModel(state.initialModel, {
      parse: false,
    });

    state.volumeMuted = Object.keys(state.workModel.volumeControl || {}).reduce(
      (obj, key) => ({ ...obj, [key]: null }),
      {}
    );
    state.volumeMutedOnVoPlay = Object.keys(
      state.workModel.onVoPlayVolumeControl || {}
    ).reduce((obj, key) => ({ ...obj, [key]: null }), {});
    state.tmpAGD = _.cloneDeep(state.workModel.agd);
    state.analyticsData = getAnalyticsData(state.initialModel);
    state.pages = getDefaultPages();
    state.rtpModel = null;
    state.applyVolumeToAll = false;
    state.narrationAssets = _.map(
      getAudioTracksBetweenSpan({
        startTime: state.workModel.displayStartTime,
        endTime: state.workModel.displayEndTime,
        audioLayer: app.rootStore.state.timemachine.trackLayer.audio,
      }),
      'data'
    );
  },

  setIanContext(state, { asset }) {
    const assets = state.ian.getAssets();
    let total = 0;
    let current = -1;

    if (!_.isEmpty(assets)) {
      total = assets.length;
      const predicate =
        state.openedFrom === 'timeline'
          ? { guid: asset.guid }
          : { id: asset.id };
      current = _.findIndex(assets, predicate);
    }

    state.ian.current = current;
    state.ian.total = total;
  },

  setLastTextOverlayEffect(state, to) {
    state.ian.instructions.useTOModel = to;
  },

  resetIanInstructions(state) {
    state.ian.instructions.editTOEffect = false;
    state.ianToModel = null;
  },

  buildIanInstructions(state, asset) {
    state.ian.instructions.editTOEffect = state.currentPageId === 'textOverlay';

    if (state.ian.instructions.editTOEffect) {
      // textOverlay effect has special inheritance - collect info about effect
      if (!_.isEmpty(asset.agd.textOverlays)) {
        state.ian.instructions.useTOModel = null;
      }
    } else {
      state.ian.instructions.useTOModel = null;
    }
  },

  setOpenedFrom(state, newSource) {
    state.openedFrom = newSource;
  },
};
